/**
 * Themed Recharts wrappers for the Progress dashboard. Ink/champagne palette,
 * gradient area fill, soft gridlines, hover tooltip. Dependency: recharts@3.
 */

import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  ComposedChart, Legend, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const GOLD = '#c9a84c';
const INK_GRID = 'rgba(255,255,255,0.07)';
const AXIS = 'rgba(255,255,255,0.45)';
const MACRO_COLORS = { protein: '#60a5fa', carbs: '#c9a84c', fat: '#f87171' };

function shortDate(d: string): string {
  // "2026-06-04" -> "Jun 4"
  const dt = new Date(d + 'T00:00:00');
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Round a chart value for display — synced metrics carry float noise. */
function fmtVal(v: number | string | undefined): string {
  if (typeof v !== 'number') return String(v ?? '');
  return String(Math.round(v * 10) / 10);
}

interface TooltipPayload { value?: number | string; }
function ChartTooltip({ active, payload, label, unit }: {
  active?: boolean; payload?: TooltipPayload[]; label?: string; unit?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--navy-dark, #0c0e14)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 8, padding: '6px 10px', fontSize: 12,
    }}>
      <div style={{ color: 'var(--gray-400)', marginBottom: 2 }}>{label ? shortDate(String(label)) : ''}</div>
      <div style={{ color: 'var(--cream, #f3ead7)', fontWeight: 600 }}>
        {fmtVal(payload[0].value)}{unit ? ` ${unit}` : ''}
      </div>
    </div>
  );
}

export interface TrendPoint { date: string; value: number; }

/** Area/line trend over time. Empty + single-point states handled gracefully. */
export function TrendChart({
  points, color = GOLD, unit, height = 200,
}: { points: TrendPoint[]; color?: string; unit?: string; height?: number }) {
  if (points.length === 0) {
    return (
      <div className="h-48 grid place-items-center text-sm" style={{ color: 'var(--gray-500)' }}>
        No data in this range yet.
      </div>
    );
  }
  if (points.length === 1) {
    const p = points[0];
    return (
      <div className="h-48 grid place-items-center text-center">
        <div>
          <p className="text-3xl font-semibold" style={{ color: 'var(--cream)' }}>{fmtVal(p.value)}{unit ? ` ${unit}` : ''}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--gray-500)' }}>{shortDate(p.date)}</p>
          <p className="text-[10px] mt-2" style={{ color: 'var(--gray-600)' }}>One data point — log more to see a trend.</p>
        </div>
      </div>
    );
  }

  const gid = `tc-${color.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={INK_GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: AXIS }}
          tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={{ fontSize: 10, fill: AXIS }} tickLine={false} axisLine={false} width={44}
          domain={['dataMin - 1', 'dataMax + 1']} allowDecimals={false}
          tickFormatter={(v) => String(Math.round(Number(v)))} />
        <Tooltip content={<ChartTooltip unit={unit} />} cursor={{ stroke: color, strokeOpacity: 0.3 }} />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2}
          fill={`url(#${gid})`} dot={{ r: 2.5, fill: color, strokeWidth: 0 }}
          activeDot={{ r: 4 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Per-day bar series (e.g. daily calories). */
export function BarSeries({
  points, color = GOLD, unit, height = 200,
}: { points: TrendPoint[]; color?: string; unit?: string; height?: number }) {
  if (points.length === 0) {
    return (
      <div className="h-48 grid place-items-center text-sm" style={{ color: 'var(--gray-500)' }}>
        No data in this range yet.
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid stroke={INK_GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: AXIS }}
          tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={{ fontSize: 10, fill: AXIS }} tickLine={false} axisLine={false} width={36} />
        <Tooltip content={<ChartTooltip unit={unit} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export interface MacroDay { date: string; calories: number; protein: number; carbs: number; fat: number; }

/**
 * Nutrition combo chart: stacked macro bars (protein/carbs/fat grams, left
 * axis) with the day's total calories as a line (right axis). One glance shows
 * both the macro split and the calorie total per day.
 */
export function MacroComboChart({ days, height = 220 }: { days: MacroDay[]; height?: number }) {
  if (days.length === 0) {
    return (
      <div className="h-48 grid place-items-center text-sm" style={{ color: 'var(--gray-500)' }}>
        No nutrition logged in this range yet.
      </div>
    );
  }
  const data = days.map((d) => ({
    date: d.date,
    protein: Math.round(d.protein),
    carbs: Math.round(d.carbs),
    fat: Math.round(d.fat),
    calories: Math.round(d.calories),
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
        <CartesianGrid stroke={INK_GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: AXIS }}
          tickLine={false} axisLine={false} minTickGap={24} />
        {/* Left axis: macro grams (the stacked bars). */}
        <YAxis yAxisId="g" tick={{ fontSize: 10, fill: AXIS }} tickLine={false} axisLine={false} width={32}
          tickFormatter={(v) => String(Math.round(Number(v)))} />
        {/* Right axis: calories (the line). */}
        <YAxis yAxisId="kcal" orientation="right" tick={{ fontSize: 10, fill: AXIS }} tickLine={false} axisLine={false} width={36}
          tickFormatter={(v) => String(Math.round(Number(v)))} />
        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          contentStyle={{ background: 'var(--navy-dark, #0c0e14)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 12 }}
          labelFormatter={(l) => shortDate(String(l))} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar yAxisId="g" dataKey="protein" stackId="m" fill={MACRO_COLORS.protein} name="Protein (g)" maxBarSize={28} />
        <Bar yAxisId="g" dataKey="carbs" stackId="m" fill={MACRO_COLORS.carbs} name="Carbs (g)" maxBarSize={28} />
        <Bar yAxisId="g" dataKey="fat" stackId="m" fill={MACRO_COLORS.fat} name="Fat (g)" radius={[3, 3, 0, 0]} maxBarSize={28} />
        <Line yAxisId="kcal" type="monotone" dataKey="calories" name="Calories" stroke="var(--cream, #f3ead7)"
          strokeWidth={2} dot={{ r: 2 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export { LineChart, Line };
